import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  listStakeholderViews,
  createStakeholderView,
  updateStakeholderView,
  deleteStakeholderView,
  rotateStakeholderToken,
} from '../../utils/stakeholderApi';
import { getOrganizationTeams } from '../../utils/teamsApi';
import { getShapeUpTags } from '../../utils/shapeUpApi';

// The phases a view can be narrowed to, in the order work moves through them.
// These keys match what the server derives, so an empty selection means "any".
const PHASES = [
  { key: 'figuring_out', label: 'Figuring out' },
  { key: 'building', label: 'Building' },
  { key: 'almost_done', label: 'Almost done' },
  { key: 'shipped', label: 'Shipped' },
];

const publicUrl = (token) => `${window.location.origin}/status/${token}`;

const emptyDraft = () => ({
  name: '',
  description: '',
  teamIds: [],
  tagSlugs: [],
  phases: [],
  includeShipped: false,
  isPublic: false,
});

// Turn a saved view row into the flat draft the dialog edits.
const draftFromView = (view) => ({
  name: view.name || '',
  description: view.description || '',
  teamIds: Array.isArray(view.filters?.teamIds) ? view.filters.teamIds : [],
  tagSlugs: Array.isArray(view.filters?.tagSlugs) ? view.filters.tagSlugs : [],
  phases: Array.isArray(view.filters?.phases) ? view.filters.phases : [],
  includeShipped: view.filters?.includeShipped === true,
  isPublic: view.isPublic === true,
});

// Flatten a draft back into the create/update payload the API expects.
const payloadFromDraft = (draft) => ({
  name: draft.name.trim(),
  description: draft.description.trim() || null,
  isPublic: draft.isPublic,
  filters: {
    teamIds: draft.teamIds,
    tagSlugs: draft.tagSlugs,
    phases: draft.phases,
    includeShipped: draft.includeShipped,
  },
});

/**
 * The admin surface for stakeholder views: the saved, plain-language lenses that
 * Operations, CX, Sales, and Support read instead of the hill chart. Editors
 * create a view (a team/tag/phase filter over one dataset), optionally publish a
 * tokenized link, and can rotate that link if it leaks.
 *
 * This is the only place a view is configured — the viewer and public pages
 * carry no edit controls by construction.
 */
const StakeholderViewsSettings = ({ organizationId, canManage = false, onNotify, onChanged }) => {
  const [views, setViews] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const notify = useCallback((message, severity = 'success') => {
    if (onNotify) onNotify(message, severity);
  }, [onNotify]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [viewsData, teamsData, tagsData] = await Promise.all([
        listStakeholderViews(organizationId),
        getOrganizationTeams(organizationId).catch(() => []),
        getShapeUpTags(organizationId).catch(() => []),
      ]);
      setViews(Array.isArray(viewsData) ? viewsData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setTags(Array.isArray(tagsData) ? tagsData : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load stakeholder views');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const teamNameById = useMemo(() => {
    const map = new Map();
    teams.forEach(team => map.set(team.id, team.name));
    return map;
  }, [teams]);

  const tagLabelBySlug = useMemo(() => {
    const map = new Map();
    tags.forEach(tag => map.set(tag.slug, tag.label));
    return map;
  }, [tags]);

  const openCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setDialogOpen(true);
  };

  const openEdit = (view) => {
    setEditingId(view.id);
    setDraft(draftFromView(view));
    setDialogOpen(true);
  };

  const togglePhase = (key) => {
    setDraft(current => ({
      ...current,
      phases: current.phases.includes(key)
        ? current.phases.filter(p => p !== key)
        : [...current.phases, key],
    }));
  };

  const handleSave = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateStakeholderView(editingId, payloadFromDraft(draft));
        notify('Stakeholder view updated.');
      } else {
        await createStakeholderView({ organizationId, ...payloadFromDraft(draft) });
        notify('Stakeholder view created.');
        // A new view flips the nav's Stakeholders link on, so refresh the org
        // counts it reads from.
        if (onChanged) onChanged();
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to save the stakeholder view', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (view) => {
    setBusyId(view.id);
    try {
      await deleteStakeholderView(view.id);
      notify('Stakeholder view deleted.');
      // Deleting the last view hides the nav link again.
      if (onChanged) onChanged();
      await fetchAll();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to delete the stakeholder view', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleRotate = async (view) => {
    setBusyId(view.id);
    try {
      await rotateStakeholderToken(view.id);
      notify('Public link rotated. The old one no longer works.');
      await fetchAll();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to rotate the link', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleCopy = (token) => {
    navigator.clipboard.writeText(publicUrl(token));
    notify('Public link copied to clipboard.');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Stakeholder views</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Plain-language "what's shipping" lenses for teams outside Product and Engineering.
        Each view is a saved filter over your live bets — a phase, a target date, and an
        on-track flag, with no Jira keys or hill mechanics. Share it with logged-in viewers,
        or publish a link anyone can open.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading stakeholder views…</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
          {views.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No stakeholder views yet. Create one to give another team a clean read on what's
              in flight.
            </Alert>
          ) : (
            <List sx={{ mb: 1 }}>
              {views.map((view) => {
                const teamNames = (view.filters?.teamIds || [])
                  .map(id => teamNameById.get(id))
                  .filter(Boolean);
                const tagNames = (view.filters?.tagSlugs || [])
                  .map(slug => tagLabelBySlug.get(slug) || slug)
                  .filter(Boolean);
                const phaseNames = (view.filters?.phases || [])
                  .map(key => PHASES.find(p => p.key === key)?.label)
                  .filter(Boolean);
                return (
                  <ListItem
                    key={view.id}
                    alignItems="flex-start"
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5, display: 'block', py: 1.5 }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{view.name}</Typography>
                            {view.isPublic
                              ? <Chip size="small" color="success" label="Public link on" />
                              : <Chip size="small" variant="outlined" label="Members only" />}
                          </Box>
                        }
                        secondary={view.description || null}
                      />
                      {canManage && (
                        <Box sx={{ display: 'flex', flexShrink: 0 }}>
                          <Tooltip title="Edit view">
                            <IconButton size="small" onClick={() => openEdit(view)} disabled={busyId === view.id}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete view">
                            <IconButton size="small" color="error" onClick={() => handleDelete(view)} disabled={busyId === view.id}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                      {teamNames.length === 0 && tagNames.length === 0 && phaseNames.length === 0 && (
                        <Typography variant="caption" color="text.secondary">All in-flight work</Typography>
                      )}
                      {teamNames.map(nameLabel => (
                        <Chip key={`team-${nameLabel}`} size="small" variant="outlined" label={nameLabel} />
                      ))}
                      {tagNames.map(nameLabel => (
                        <Chip key={`tag-${nameLabel}`} size="small" variant="outlined" label={`#${nameLabel}`} />
                      ))}
                      {phaseNames.map(nameLabel => (
                        <Chip key={`phase-${nameLabel}`} size="small" variant="outlined" label={nameLabel} />
                      ))}
                      {view.filters?.includeShipped && (
                        <Chip size="small" variant="outlined" label="Includes shipped" />
                      )}
                    </Stack>

                    <Box sx={{ mt: 1 }}>
                      <Link
                        href={`/shape-up/stakeholders/${view.id}`}
                        variant="caption"
                        underline="hover"
                      >
                        Open the in-app view →
                      </Link>
                    </Box>

                    {view.isPublic && view.token && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          value={publicUrl(view.token)}
                          InputProps={{ readOnly: true }}
                          sx={{ flexGrow: 1, minWidth: 240 }}
                        />
                        <Tooltip title="Copy public link">
                          <IconButton size="small" onClick={() => handleCopy(view.token)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canManage && (
                          <Tooltip title="Rotate link (kills the old one)">
                            <span>
                              <IconButton size="small" onClick={() => handleRotate(view)} disabled={busyId === view.id}>
                                <AutorenewIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}

          {canManage && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
              New stakeholder view
            </Button>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit stakeholder view' : 'New stakeholder view'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            autoFocus
            label="Name"
            placeholder="Operations"
            value={draft.name}
            onChange={(e) => setDraft(current => ({ ...current, name: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Description (optional)"
            placeholder="What this team should watch for."
            value={draft.description}
            onChange={(e) => setDraft(current => ({ ...current, description: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            size="small"
            options={teams}
            getOptionLabel={(team) => team.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={teams.filter(team => draft.teamIds.includes(team.id))}
            onChange={(_e, selected) => setDraft(current => ({ ...current, teamIds: selected.map(t => t.id) }))}
            renderInput={(params) => (
              <TextField {...params} label="Teams" placeholder="All teams" helperText="Leave empty for every team" />
            )}
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            size="small"
            options={tags}
            getOptionLabel={(tag) => tag.label || ''}
            isOptionEqualToValue={(option, value) => option.slug === value.slug}
            value={tags.filter(tag => draft.tagSlugs.includes(tag.slug))}
            onChange={(_e, selected) => setDraft(current => ({ ...current, tagSlugs: selected.map(t => t.slug) }))}
            renderInput={(params) => (
              <TextField {...params} label="Tags" placeholder="Any tag" helperText="Match bets carrying any of these tags" />
            )}
            sx={{ mb: 1 }}
          />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Phases to show</Typography>
          <Typography variant="caption" color="text.secondary">Leave all unchecked to show every phase.</Typography>
          <FormGroup row sx={{ mb: 1 }}>
            {PHASES.map(phase => (
              <FormControlLabel
                key={phase.key}
                control={
                  <Checkbox
                    size="small"
                    checked={draft.phases.includes(phase.key)}
                    onChange={() => togglePhase(phase.key)}
                  />
                }
                label={phase.label}
              />
            ))}
          </FormGroup>

          <FormControlLabel
            control={
              <Switch
                checked={draft.includeShipped}
                onChange={(e) => setDraft(current => ({ ...current, includeShipped: e.target.checked }))}
              />
            }
            label="Include shipped/closed bets"
          />
          <FormControlLabel
            sx={{ display: 'block', mt: 1 }}
            control={
              <Switch
                checked={draft.isPublic}
                onChange={(e) => setDraft(current => ({ ...current, isPublic: e.target.checked }))}
              />
            }
            label="Publish a public link (no login required)"
          />
          {draft.isPublic && (
            <Alert severity="info" sx={{ mt: 1 }}>
              A public link is generated on save. Anyone with the link can read this view. You can
              rotate it later to kill a leaked link.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !draft.name.trim()}>
            {saving ? <CircularProgress size={20} /> : (editingId ? 'Save changes' : 'Create view')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StakeholderViewsSettings;
